import React, { FC } from 'react';
import { Alert } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import ActionLink from '../../../components/ActionLink/ActionLink';
import BaseTable, { linkColumn } from '../../../components/BaseTable/BaseTable';
import FAIcon from '../../../components/FAIcon/FAIcon';
import Loading from '../../../components/Loading/Loading';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { permConst } from '../../../constants/permConst';
import { transferRightsConstants } from '../../../constants/transferRightsConstants';
import { platformFeatures } from '../../../constants/platformConstants';
import { useUserProfile } from '../../../hooks/reduxHooks';
import { getDigitalCatalog } from '../../../services/digitalCodesService';
import { formatCurrency } from '../../../utils/currencyUtils';
import { getCurrentRequest } from '../../../utils/digitalCodesUtils';
import { isAuthorized, isAuthorizedForUrl, isFeatureActiveForPlatform } from '../../../utils/userUtils';
import StatusText from '../../../components/StatusText/StatusText';

const DUPLICATE = '__DUPLICATE__';

interface DigitalCatalogTabProps {
	productId?: number;
	product: {
		agreement_family: string;
		platform_code: PlatformCode;
		retail_status?: string;
	};
	isSelected: boolean;
	flagIssue: () => void;
	rightsHolders: ProductData['distribution'];
}

interface PriceStatusProps {
	price?: string;
	status: string;
	isDuplicate?: boolean;
}

const DigitalCatalogTab: FC<DigitalCatalogTabProps> = ({
	productId,
	product,
	isSelected,
	flagIssue,
	rightsHolders,
}: DigitalCatalogTabProps) => {
	const userProfile = useUserProfile();
	const catalogQuery = useQuery(
		['getDigitalCatalog', productId],
		() => getDigitalCatalog(String(productId)),
		{
			onSuccess: ({ data }) => {
				if (data?.components.some((item) => item.retail_prices?.length > 1)) {
					flagIssue();
				}
			},
		},
	);
	
	const components = catalogQuery.data?.data.components;
	const fault = catalogQuery.isError && catalogQuery.error;

	const digitalNDIDCompanyId = rightsHolders?.find(
		(rh) => rh.right_type === transferRightsConstants.DISTRIBUTION_TYPE.DIGITAL,
	)?.ndid_company_id;
	const ownerNDIDCompanyId = rightsHolders?.find(
		(rh) => rh.right_type === transferRightsConstants.DISTRIBUTION_TYPE.OWNERSHIP,
	)?.ndid_company_id;
	const isDigitalPub =
		isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.COMPONENT.CIB.REQUEST.VIEW.COMPANY,
			permConst.PRODUCT.COMPONENT.DECLARED_PRICE.REQUEST.VIEW.COMPANY,
		]) && userProfile.companyId === digitalNDIDCompanyId;
	const isOwnerOnly =
		isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.COMPONENT.CIB.REQUEST.VIEW.COMPANY,
			permConst.PRODUCT.COMPONENT.DECLARED_PRICE.REQUEST.VIEW.COMPANY,
		]) &&
		userProfile.companyId !== digitalNDIDCompanyId &&
		userProfile.companyId === ownerNDIDCompanyId;

	if (!isSelected) {
		return null;
	}

	const canSeeCIBPrice =
		isFeatureActiveForPlatform(
			userProfile,
			platformFeatures.CODE_IN_BOX,
			product?.platform_code,
			'view',
		);

	const allowLinkToComponentDetail =
		isAuthorizedForUrl(userProfile, '/products/components/:nsUid') &&
		// user can view all
		(isAuthorized(userProfile.permissions, [permConst.PRODUCT.COMPONENT.VIEW.ALL]) ||
			// or user is the digital publisher or owner and has agreements
			(isAuthorized(userProfile.permissions, [permConst.PRODUCT.COMPONENT.VIEW.COMPANY]) &&
				(isDigitalPub || isOwnerOnly) &&
				isFeatureActiveForPlatform(
					userProfile,
					platformFeatures.DIGITAL_CODES,
					product.platform_code,
					'view',
				)));

	const isLoading = catalogQuery.isLoading;

	const tableFormat = [
		{ Header: 'NSUID', accessor: 'ns_uid', width: 50 },
		{ Header: 'Component Name', accessor: 'game_name', width: 200 },
		{
			Header: 'Retail Pricing Method',
			id: 'retail_price',
			width: 50,
			accessor: (row: ArrayElement<NonNullable<typeof components>>) => {
				const { retail_prices, declared_price_requests } = row;
				const isDuplicate = retail_prices && retail_prices?.length > 1;
				
				// Check for active retail prices (including when there are duplicates)
				if (retail_prices && retail_prices.length > 0) {
					return {
						status: 'ACTIVE',
						price: retail_prices[0].price,
						isDuplicate,
					};
				}

				const currentRequest = getCurrentRequest(declared_price_requests) as
					| ArrayElement<typeof declared_price_requests>
					| undefined;
				if (currentRequest) {
					return {
						price:
							currentRequest?.rollup_status === 'IN PROCESS'
								? undefined
								: currentRequest.certified_price,
						status:
							currentRequest?.rollup_status === 'IN PROCESS'
								? 'IN PROCESS'
								: 'DECLARED',
						isDuplicate,
					};
				} else if (isDuplicate) {
					// Return duplicate flag even when there's no current request
					return {
						status: DUPLICATE,
						price: undefined,
						isDuplicate: true,
					};
				} else {
					return null;
				}
			},
			sortMethod: (a: PriceStatusProps, b: PriceStatusProps) => {
				return parseFloat(a?.price === undefined ? '0' : a?.price) >
					parseFloat(b?.price === undefined ? '0' : b?.price)
					? 1
					: -1;
			},
			Cell: ({ value }: { value: PriceStatusProps | null }) => {
				if (!value) {
					return <></>;
				}
				
				const duplicateWarning = value?.isDuplicate ? (
					<div>
						<b style={{ color: 'red' }}>Duplicate</b>
					</div>
				) : null;
				
				if (value?.status === DUPLICATE) {
					return duplicateWarning;
				} else if (value.status === 'ACTIVE') {
					return (
						<>
							{duplicateWarning}
							<div>
								<StatusText badge>PERMANENT</StatusText>
								<span style={{ marginLeft: 4 }}>
									{formatCurrency(value.price)}
								</span>{' '}
							</div>
						</>
					);
				} else if (value.status === 'IN PROCESS') {
					return (
						<>
							{duplicateWarning}
							<div>
								<StatusText variant="warning" badge>
									IN PROCESS
								</StatusText>
							</div>
						</>
					);
				} else if (value.status === 'DECLARED') {
					return (
						<>
							{duplicateWarning}
							<div>
								<StatusText badge>{value.status}</StatusText>
								<span style={{ marginLeft: 4 }}>
									{formatCurrency(value.price)}
								</span>{' '}
							</div>
						</>
					);
				} else {
					return <></>;
				}
			},
		},
		...(canSeeCIBPrice
			? [
				{
					Header: 'Code-in-Box Pricing Method',
					id: 'cib_price',
					width: 55,
					accessor: (row: ArrayElement<NonNullable<typeof components>>) => {
						const { cib_price_requests } = row;
						return getCurrentRequest(cib_price_requests);
					},
					sortMethod: (
						a: CIBPriceRequestData,
						b: CIBPriceRequestData,
					) => {
						return parseFloat(
								a?.certified_price === undefined ? '0' : a?.certified_price,
						) >
								parseFloat(
									b?.certified_price === undefined ? '0' : b?.certified_price,
								)
							? 1
							: -1;
					},
					Cell: ({ value }: { value: CIBPriceRequestData }) => {
						if (!value) {
							return null;
						}
						if (value?.rollup_status === 'IN PROCESS') {
							return (
									<div>
										<StatusText variant="warning" badge>
											IN PROCESS
										</StatusText>
									</div>
							);
						} else {
							return (
								<div>
									<StatusText badge>
										{value?.price_type_code === 'WHOLESALE'
											? 'WHOLESALE'
											: 'ROM SIZE'}
									</StatusText>
									<span style={{ marginLeft: 4 }}>
										{value?.price_type_code === 'WHOLESALE'
											? formatCurrency(value?.certified_price)
											: null}
									</span>{' '}
								</div>
							);
						}
					},
				},
			  ]
			: []),
		{
			id: 'retractable',
			accessor: (row: ArrayElement<NonNullable<typeof components>>) => {
				const { cib_price_requests } = row;
				return getCurrentRequest(cib_price_requests)?.retractable ?? null;
			},
			show: false,
		},
		...(allowLinkToComponentDetail
			? [
				{
					...linkColumn,
					Cell: ({ original }: { original: { ns_uid: string } }) => (
							<Link to={`/products/components/${original.ns_uid}`}>
								<FAIcon name="chevron-right" />
							</Link>
					),
				},
			  ]
			: []),
	];
	const titleComponents = components?.filter(({ content_type }) => content_type === 'TITLE');
	const bundleComponents = components?.filter(({ content_type }) => content_type === 'BUNDLE');
	const otherComponents = components?.filter(
		({ content_type }) => content_type !== 'TITLE' && content_type !== 'BUNDLE',
	);

	return (
		<>
			{isLoading ? (
				<Loading />
			) : (
				<>
					{fault ? (
						<Alert variant="danger">
							There was an error while loading digital catalog data, which is
							necessary for this tab.{' '}
							<ActionLink
								onClick={() => catalogQuery.refetch({ cancelRefetch: true })}
							>
								Retry load
							</ActionLink>
							<FAIcon size="xs" name="chevron-right" />
						</Alert>
					) : !components?.length ? (
						<Alert variant="warning">No digital items were found</Alert>
					) : (
						catalogQuery.isSuccess && (
							<>
								{(['BEE', 'POT', 'HAC'].includes(product.platform_code)) && (
									<Alert className="alert-icon" variant="warning">
										To establish pricing for Code-in-Box orders you must choose the pricing method for the component. Select the <FAIcon name="chevron-right" /> to set the method and submit a pricing request.
									</Alert>
								)}
								{!!titleComponents?.length && (
									<>
										<SectionTitle>Main Title</SectionTitle>
										<BaseTable
											className="mb-3"
											columns={tableFormat}
											data={titleComponents}
											showPagination={titleComponents.length > 10}
											sortable={false}
											allowOverflow
										/>
									</>
								)}
								{!!bundleComponents?.length && (
									<>
										<SectionTitle>Bundles</SectionTitle>
										<BaseTable
											className="mb-3"
											columns={tableFormat}
											data={bundleComponents}
											showPagination={bundleComponents.length > 10}
											allowOverflow
										/>
									</>
								)}
								{!!otherComponents?.length && (
									<>
										<SectionTitle>Other Components</SectionTitle>
										<BaseTable
											className="mb-3"
											columns={tableFormat}
											data={otherComponents}
											showPagination={otherComponents.length > 10}
											allowOverflow
										/>
									</>
								)}
							</>
						)
					)}
					<div className="digital-codes-note">
						<p>
							Your product that can be ordered as digital codes are listed on this
							tab.
						</p>

						<p>
							If this page is blank the NCMS entry for this product has not progressed
							far enough for codes to be ordered through the system.
						</p>

						<p>
							Items indicated as <b>Duplicate</b> have a pricing error. The pricing
							error will need to be resolved before codes can be ordered. Please
							contact us to correct the error.
						</p>

						<p>
							Digital items must have a permanent price entry to be ordered. Price
							entries in NCMS will appear on this page the day after the price goes
							live. Your product and price can remain hidden in the eShop during the
							Digital Code ordering process. For more Digital Code order information
							see the{' '}
							<Link to="/orders/digital-codes">Orders/Digital Code Orders</Link> menu.
						</p>
					</div>
				</>
			)}
		</>
	);
};

export default DigitalCatalogTab;
